import { Space } from 'antd';
import React from 'react';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { Field } from 'app/types/interfaces/Field';
import { FIELD_TYPES } from 'app/types';
import { DataType } from 'app/providers/StaticProvider';
import moment from 'moment';
import omit from 'lodash/omit';

export const RequirementFields: Array<Field> = [
  {
    label: 'Inspection Date',
    dataIndex: 'inspectionDate',
    type: FIELD_TYPES.DATE,
    render: (value: string) => {
      return value || 'N/A';
    }
  },
  {
    label: 'Inspection Type',
    dataIndex: 'typeOfService',
    type: FIELD_TYPES.SELECT,
    staticType: DataType.INSPECTION_TYPE,
    render: (value: string) => {
      return <>{value}</>;
    }
  },
  {
    label: 'Inspection Level',
    dataIndex: 'generalInspectLevel',
    type: FIELD_TYPES.SELECT,
    staticType: DataType.INSPECTION_LEVEL,
    render: (value: string) => {
      return <>{value}</>;
    }
  },
  {
    label: 'AQL',
    dataIndex: '',
    type: FIELD_TYPES.ROW,
    cols: [
      {
        label: 'Critical Level',
        dataIndex: 'critical',
        type: FIELD_TYPES.SELECT,
        staticType: DataType.AQL,
        span: 8
      },

      {
        label: 'Major Level',
        dataIndex: 'major',
        type: FIELD_TYPES.SELECT,
        staticType: DataType.AQL,
        span: 8
      },
      {
        label: 'Minor Level',
        dataIndex: 'minor',
        type: FIELD_TYPES.SELECT,
        staticType: DataType.AQL,
        span: 8
      }
    ],
    render: (_: string, record) => {
      return `Critical ${record?.criticalLevel} Major ${record?.criticalMajorLevel} Minor ${record?.criticalMinorLevel}`;
    }
  }
];

export const AdditionalFields: Array<Field> = [
  {
    label: 'Files',
    dataIndex: 'files',
    type: FIELD_TYPES.UPLOAD,
    formItemProps: {
      //This is Form Item wrap component;
      valuePropName: 'fileList',
      getValueFromEvent: (args) => {
        if (Array.isArray(args)) return args;
        return args.fileList;
      }
    },
    fieldProps: {
      beforeUpload: () => {
        //If you don't want to auto upload just return false
        return false;
      },

      dragger: true,
      multiple: true,
      accept: 'image/*,.pdf'
    }
  },
  {
    label: '',
    dataIndex: '',
    type: FIELD_TYPES.TITLE,
    fieldProps: {
      level: 5,
      children: '4b. Remarks'
    }
  },
  {
    label: 'Please specify if there is any other special requirement.',
    dataIndex: 'remark',
    type: FIELD_TYPES.TEXTAREA,
    fieldProps: {
      showCount: true
    }
  }
];

export const DescriptionReportDetailFields: Array<
  Array<{
    label: string;
    dataIndex: string;
    render: (value: string | number | any, record?: any) => React.ReactNode;
  }>
> = [
  [
    {
      label: 'PO #:',
      dataIndex: 'poNo',
      render: (value: string) => {
        return <>{value}</>;
      }
    },
    {
      label: 'Order Qty:',
      dataIndex: 'orderQty',
      render: (value: string) => {
        return <>{value}</>;
      }
    },
    {
      label: '# of Cartons:',
      dataIndex: 'cartonsNo',
      render: (value: string) => {
        return <>{value}</>;
      }
    }
  ],
  [
    {
      label: 'Sample size:',
      dataIndex: 'sampleSize',
      render: (value: string) => {
        return <>{value}</>;
      }
    },
    {
      label: 'AQL:',
      dataIndex: '',
      render: (value: string, record) => {
        return (
          <Space>
            <span>Critical {record?.critical}</span>
            <span>Major {record?.major}</span>
            <span>Minor {record?.minor}</span>
          </Space>
        );
      }
    }
  ],
  [
    {
      label: 'Customer Name:',
      dataIndex: 'clientName',
      render: (value: string) => {
        return <>{value}</>;
      }
    },
    {
      label: 'Customer PO #:',
      dataIndex: 'clientPONo',
      render: (value: string) => {
        return <>{value}</>;
      }
    }
  ],
  [
    {
      label: 'Signed Sample Available :',
      dataIndex: 'isSignSample',
      render: (value: string) => {
        return <>{value ? 'Yes' : 'No'}</>;
      }
    }
  ]
];

export const DescriptionFactoryFields: Array<
  Array<{
    label: string;
    dataIndex: string;
    render: (value: string | number | any, record?: any) => React.ReactNode;
  }>
> = [
  [
    {
      label: 'Contact Person #:',
      dataIndex: 'contactPerson',
      render: (value: string, record) => {
        if (!value) {
          return get(record, 'factory.contactPerson') || 'N/A';
        }
        return <>{value}</>;
      }
    },
    {
      label: 'Contact no. ',
      dataIndex: 'contactPersonPhone',
      render: (value: string, record) => {
        if (!value) {
          return get(record, 'factory.contactNumber') || 'N/A';
        }
        const label = [record?.areaCode, value];
        return <>{label.join(' ')}</>;
      }
    }
  ],
  [
    {
      label: 'Email Address:',
      dataIndex: 'contactPersonEmail',
      render: (value: string, record) => {
        if (!value) {
          return (
            get(record, 'factory.contactEmails')
              ?.map(({ email }) => email)
              .join?.(', ') || 'N/A'
          );
        }
        return <>{value}</>;
      }
    }
  ]
];

export const InspectionFields = [[{}, {}, {}]];

export const parserBookInspection = (detail: any) => {
  const products = get(
    detail,
    ['inspectionPOReportGroup', 'inspectionPOItems'],
    []
  );
  const assignTo = get(detail, ['assignTo'], []);
  const factory = get(detail, ['inspectionFactory'], {});
  const requirement = pick(detail, [
    'inspectionDate',
    'criticalLevel',
    'criticalMinorLevel',
    'criticalMajorLevel',
    'typeOfService',
    'generalInspectLevel',
    'inspectionEndDate',
    'noOfManday',
    'status'
  ]);
  const additional = pick(detail, ['remark']);
  const { documentList } = pick(detail, ['documentList'], {} as any);
  return {
    products: products,
    factory: factory,
    inspectionRequirement: {
      inspectionType: requirement.typeOfService ?? '',
      inspectionLevel: requirement.generalInspectLevel,
      critical: requirement.criticalLevel,
      major: requirement.criticalMajorLevel,
      minor: requirement.criticalMinorLevel,
      noOfManday: requirement.noOfManday,
      inspectionStartDate: requirement?.inspectionDate
        ? moment(requirement?.inspectionDate)
        : null,
      inspectionEndDate: requirement?.inspectionEndDate
        ? moment(requirement?.inspectionEndDate)
        : null
    },
    additionalInformation: {
      ...additional,
      files: (documentList || []).map((file: any) => ({
        ...file,
        name: file.fileName
      }))
    },
    assignTo: assignTo.map((assigned: any) => {
      return {
        ...assigned,
        // inspectorName: `${assigned.firstName} ${assigned.lastName}`,
        inspector: {
          ...assigned,
          id: assigned.userId,
          value: assigned.userId,
          label: `${assigned.firstName} ${assigned.lastName}`
        },
        userId: assigned.userId,
        inspectionDates: assigned.inspectionDates?.map(
          (inspectionDate: any) => {
            return {
              date: moment(inspectionDate.date),
              manDay: inspectionDate.manday
            };
          }
        )
      };
    }),
    status: requirement?.status || 'waitingAccept'
  };
};

export const createDefaultInspection = (product: any) => {
  return {
    productProfile: product,
    isSignSample: 0,
    clientPONo: '',
    clientName: '',
    cartonsNo: '',
    orderQty: ''
  };
};
const mergeValue = ([firstValue, secondValue, thirdValue]: any[]) => {
  const { products } = firstValue;
  const { products: productsWithInspectionCriteria } = secondValue;
  const newProducts = products.map((product: any, index: number) => {
    const existed = (productsWithInspectionCriteria || [])[index];
    const inspectionCriteria = get(existed, 'inspectionCriteria', null);
    return {
      ...product,
      inspectionCriteria: {
        ...inspectionCriteria,
        isSaveAsNew: inspectionCriteria?.inspectionOption === 'save_as_new',
        isOnlyApplyThisJob:
          inspectionCriteria?.inspectionOption === 'only_apply_this_job'
      }
    };
  });
  return [
    { ...firstValue, products: newProducts },
    {
      products: newProducts
    },
    thirdValue
  ];
};
export const transformsParams = (
  stepValues: Array<any>,
  initialValues: any,
  uploadedFiles: Array<any>
) => {
  const mergedValue = mergeValue(stepValues);
  const transformStep1 = (value: any) => {
    if (!value) return {};
    const { factory, inspectionRequirement, additionalInformation } = value;

    return {
      inspectionFactory: {
        ...omit(factory, 'factory'),
        factoryId: factory?.factory?.id
      },
      inspectionRequirement: {
        inspectionDate:
          inspectionRequirement?.inspectionStartDate?.format('YYYY-MM-DD'),
        typeOfService: inspectionRequirement.inspectionType,
        generalInspectLevel: inspectionRequirement.inspectionLevel || 'na',
        criticalLevel: inspectionRequirement.critical,
        criticalMajorLevel: inspectionRequirement.major,
        criticalMinorLevel: inspectionRequirement.minor
      },
      addionalInformation: {
        documentListIds: uploadedFiles.map(({ id }) => id),
        remark: additionalInformation.remark
      },
      status: inspectionRequirement?.status || 'waitingAccept'
    };
  };
  const transformStep2 = (value: any) => {
    if (!value) return {};
    const { products } = value;
    return {
      inspectionPOReportGroup: {
        id: initialValues?.inspectionPOReportGroup.id,
        inspectionPOItems: products.map(
          ({
            inspectionCriteria,
            isSignSample,
            productProfile,
            ...item
          }: any) => {
            return {
              ...omit(item, 'productProfile'),
              inspectionCriteria: {
                ...inspectionCriteria,
                isSaveAsNew:
                  inspectionCriteria?.inspectionOption === 'save_as_new',
                isOnlyApplyThisJob:
                  inspectionCriteria?.inspectionOption === 'only_apply_this_job'
              },
              isSignSample: isSignSample !== 0,
              productProfileId: productProfile.id
            };
          }
        )
      }
    };
  };
  const transformStep3 = (value: any) => {
    if (!value) return {};

    return {
      inspectionRequirement: {
        ...omit(value.inspectionRequirement, 'inspectionStartDate'),
        inspectionDate:
          value.inspectionRequirement?.inspectionStartDate?.format(
            'YYYY-MM-DD'
          ),
        inspectionEndDate:
          value.inspectionRequirement?.inspectionEndDate?.format(
            'YYYY-MM-DD'
          ) ?? null
      },
      assignTo: value.assignTo.map((assigned: any) => {
        return {
          inspectionDates: assigned.inspectionDates.map(
            (inspectionDate: any) => {
              return {
                date: inspectionDate.date.format('YYYY-MM-DD'),
                manday: inspectionDate.manDay
              };
            }
          ),

          userId: assigned.userId
        };
      })
    };
  };

  const params = mergedValue.reduce((acc, current, step) => {
    let stepParams = { ...acc };
    switch (step) {
      case 0: {
        stepParams = { ...stepParams, ...transformStep1(current) };
        break;
      }
      case 1: {
        stepParams = { ...stepParams, ...transformStep2(current) };
        break;
      }
      default: {
        // Last step just only have inspectors and manDay,startDate and EndDate.
        const { inspectionRequirement } = transformStep3(current);
        stepParams = {
          ...stepParams,
          ...omit(transformStep3(current), 'inspectionRequirement'),
          inspectionRequirement: {
            ...stepParams.inspectionRequirement,
            ...inspectionRequirement
          }
        };
        break;
      }
    }
    return stepParams;
  }, {});
  return params;
};
